"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeroSection from "./components/KendallHero";
import NeighborhoodStats from "@/components/feature/NeighborhoodStats";
import RepairServicesGrid from "@/components/feature/RepairServicesGrid";
import ScopeDisclaimer from "@/components/feature/ScopeDisclaimer";
import NeighborhoodCta from "@/components/feature/NeighborhoodCta";
import QuartierCrossLinks from "@/components/feature/QuartierCrossLinks";
import { injectMetaTags } from "@/lib/seoHelpers";
import FaqSection from "@/components/feature/FaqSection";
import RelatedServicesBlock from "@/components/feature/RelatedServicesBlock";
import NearbyAreasBlock from "@/components/feature/NearbyAreasBlock";
import AboutAreaSection from "@/components/feature/AboutAreaSection";

const neighborhood = "Kendall";

export default function KendallPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const kendallFaqs = [
    { question: t("hood.faq1Q"), answer: t("hood.faq1A") },
    { question: t("hood.faq2Q"), answer: t("hood.faq2A") },
    { question: t("hood.faq3Q"), answer: t("hood.faq3A") },
    { question: t("hood.faq4Q"), answer: t("hood.faq4A") },
    { question: t("kendall.faq5Q"), answer: t("kendall.faq5A") },
  ];

  useEffect(() => {
    document.title =
      currentLang === "es"
        ? "Reparaci\u00F3n de Ventanas Kendall \u2014 Casas Familiares Suburbanas"
        : "Kendall Window Repair \u2014 Suburban Family Home Specialists";

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.content =
      currentLang === "es"
        ? "Especialistas en reparaci\u00F3n de ventanas en Kendall. Casas familiares suburbanas con tipos variados. Servicio de herrajes single-pane, double-pane y modernos con impacto Miami-Dade."
        : "Kendall window repair specialists. Suburban family homes with varied window types. Single-pane, double-pane, and modern impact hardware service Miami-Dade.";

    const cleanupSeo = injectMetaTags({
      title: document.title,
      description: metaDesc.content,
      url: "https://impact-window-repair-miami.com/impact-windows-kendall/",
      image: "https://impact-window-repair-miami.com/og/og-kendall.jpg",
      imageAlt:
        currentLang === "es"
          ? "Reparaci\u00F3n de ventanas Kendall Miami"
          : "Window repair Kendall Miami",
      breadcrumbs: [
        { name: currentLang === "es" ? "Inicio" : "Home", item: "https://impact-window-repair-miami.com/" },
        { name: currentLang === "es" ? "\u00C1reas de Servicio" : "Service Areas", item: "https://impact-window-repair-miami.com/service-areas/" },
        { name: neighborhood, item: "https://impact-window-repair-miami.com/impact-windows-kendall/" },
      ],
      type: "website",
    });

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: "Window Repair",
          provider: {
            "@type": "LocalBusiness",
            name: "Windows and Doors Repair LLC",
            telephone: "+17863774452",
            email: "contact@impact-window-repair-miami.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "848 Brickell Ave, PH-5",
              addressLocality: "Miami",
              addressRegion: "FL",
              postalCode: "33131",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 25.7617,
              longitude: -80.1918,
            },
            openingHoursSpecification: [{
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "07:00",
              closes: "21:00",
            }],
            priceRange: "$$",
            paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
            currenciesAccepted: "USD",
            areaServed: [
              { "@type": "City", name: "Miami" },
              { "@type": "AdministrativeArea", name: "Miami-Dade County" },
              "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach",
            ],
            knowsLanguage: ["en", "es"],
            knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
            slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          },
          areaServed: {
            "@type": "City",
            name: "Kendall, Miami-Dade, FL",
          },
          serviceType: "Window Repair",
          description:
            currentLang === "es"
              ? "Reparaci\u00F3n de herrajes, vidrios y mantenimiento en Kendall."
              : "Hardware repair, glass replacement, and maintenance in Kendall.",
        },
        {
          "@type": "LocalBusiness",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 25.7617,
            longitude: -80.1918,
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "07:00",
            closes: "21:00",
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            { "@type": "City", name: "Miami" },
            { "@type": "AdministrativeArea", name: "Miami-Dade County" },
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach",
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          url: window.location.href,
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: kendallFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      const existingFaq = document.getElementById("faq-schema");
      if (existingFaq) document.head.removeChild(existingFaq);
    };
  }, [currentLang]);

  return (
    <main className="min-h-screen">
      <HeroSection
        h1={t("kendall.h1")}
        subhead={t("kendall.subhead")}
        zips={["33176", "33186", "33193", "33196"]}
      />
      <AboutAreaSection
        badge={currentLang === "es" ? "REPARACI\u00D3N EN KENDALL" : "KENDALL WINDOW REPAIR"}
        imageSrc="https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/808ecbf2-f97b-4e21-a19b-8baf0e9a8569_Kendall-Window-Repair-for-Suburban-Homes.jpg?v=720cddd988b7a8df0e83fe212c2e5251"
        imageAlt={currentLang === "es" ? "Especialista en reparaci\u00F3n de ventanas Kendall Miami" : "Kendall Miami window hardware repair specialist servicing suburban family home"}
        heading={currentLang === "es" ? "Reparaci\u00F3n de Ventanas para Casas Familiares en Kendall" : "Kendall Window Repair for Suburban Homes"}
        lead={t("kendall.intro")}
        bullets={
          currentLang === "es"
            ? [
                "Reparaci\u00F3n de herrajes en tipos variados de ventanas",
                "Reemplazo de vidrios en marcos antiguos no impacto",
                "Renovaci\u00F3n de burletes para ahorro energ\u00E9tico",
              ]
            : [
                "Hardware repair on varied window types",
                "Glass replacement in older non-impact frames",
                "Weatherstripping renewal for energy savings",
              ]
        }
        closing={
          currentLang === "es"
            ? "Las casas familiares de Kendall se benefician m\u00E1s de planes de mantenimiento anual cubriendo todas las ventanas en una visita."
            : "Kendall family homes benefit most from annual maintenance plans covering all windows in one visit."
        }
      />
      <NeighborhoodStats />
      <RepairServicesGrid heading={t("hood.commonH2")} />
      <ScopeDisclaimer neighborhood={neighborhood} />
      <FaqSection heading={t("hood.commonFaqH2")} items={kendallFaqs} />
      <RelatedServicesBlock neighborhood={neighborhood} />
      <NearbyAreasBlock neighborhood={neighborhood} neighbors={[
        { name: currentLang === "es" ? "Pinecrest" : "Pinecrest", slug: "pinecrest" },
        { name: currentLang === "es" ? "South Miami" : "South Miami", slug: "south-miami" },
        { name: currentLang === "es" ? "Doral" : "Doral", slug: "doral" },
      ]} />
      <NeighborhoodCta neighborhood={neighborhood} heading={t("hood.commonCta")} />
      <QuartierCrossLinks />
    </main>
  );
}